import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'
import { PrimeIcons } from 'primereact/api'
import { Button } from 'primereact/button'
import { FileUpload, ItemTemplateOptions } from 'primereact/fileupload'
import { Message } from 'primereact/message'
import { classNames } from 'primereact/utils'

import { Constants } from '../../../../../../utils/constants'
import {
  HandleableError,
  getHandleableError,
} from '../../../../../../utils/errors/isXiaError'
import { isEmpty } from '../../../../../../utils/helper/isEmpty'
import { SupplierItemList } from '../../../data'
import { requestPrepareCsvFile } from '../../../data/api'
import {
  UploadPanelFormValues,
  UploadPanelFormValuesNames,
} from './common/UploadPanelFormValues'

type Props = {
  goToNextStep: () => void
  setCsvData: React.Dispatch<React.SetStateAction<SupplierItemList | null>>
  setError: React.Dispatch<React.SetStateAction<HandleableError | null>>
  setFooter: (content: React.ReactNode) => void
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
}

export const UploadPanel: React.FC<Props> = ({
  goToNextStep,
  setCsvData,
  setError,
  setFooter,
  setIsLoading,
}) => {
  const { t, i18n } = useTranslation()

  // Formik
  const formik = useFormik<UploadPanelFormValues>({
    initialValues: {
      csv: undefined,
    },
    validate: (formData: UploadPanelFormValues) => {
      let errors: FormikErrors<UploadPanelFormValues> = {}
      if (isEmpty(formData.csv))
        errors.csv = t(
          'masterDataManagement.packagingData.upload.dialog.errors.required',
          {
            fieldName: t(
              'masterDataManagement.packagingData.upload.dialog.uploadPanel.fields.csv',
            ),
          },
        )

      return errors
    },
    onSubmit: (formData: UploadPanelFormValues) => {
      onUploadCsv(formData)
    },
  })

  const isFormFieldValid = (name: UploadPanelFormValuesNames) =>
    !(formik.touched[name] && formik.errors[name])

  const getFormErrorMessage = (name: UploadPanelFormValuesNames) => {
    return (
      !isFormFieldValid(name) && (
        <small className="p-error">{formik.errors[name]}</small>
      )
    )
  }

  // Footer
  useEffect(() => {
    setFooter(
      <div className="flex pt-4 justify-content-end">
        <Button
          label={t('suppliers.table.upload.dialog.uploadPanel.footer.next')}
          icon={PrimeIcons.ARROW_RIGHT}
          iconPos="right"
          onClick={formik.submitForm}
        />
      </div>,
    )
  }, [])

  // Upload csv
  const uploadCsv = async (csv: File) => {
    try {
      setIsLoading(true)
      const result = await requestPrepareCsvFile(csv)
      setCsvData(result)
    } catch (error) {
      const handleableError = getHandleableError(error)
      setError(handleableError)
    } finally {
      setIsLoading(false)
    }
  }

  const onUploadCsv = (data: UploadPanelFormValues) => {
    if (data.csv === undefined) return

    uploadCsv(data.csv)
    goToNextStep()
  }

  // Messages content
  const downloadSample = () => {
    const fileURL = `/files/sample_suppliers_${i18n.language}.csv`
    const fileLink = document.createElement('a')
    fileLink.href = fileURL
    fileLink.setAttribute(
      'download',
      `suppliers_sample_${i18n.language.toUpperCase()}.csv`,
    )
    document.body.appendChild(fileLink)
    fileLink.click()
    document.body.removeChild(fileLink)
  }

  const fileItemTemplate = (file: any, options: ItemTemplateOptions) => {
    return (
      <div className="flex align-items-center flex-wrap">
        <div className="flex align-items-center">
          <img alt={''} src={file.objectURL} width={100} />
          <span className="flex flex-column text-left">
            <span className="font-bold">{file.name}</span>
            <span className="text-sm mt-1">{options.formatSize}</span>
            <span className="text-sm mt-1">
              {new Date().toLocaleDateString()}
            </span>
          </span>
        </div>
        <Button
          type="button"
          icon="pi pi-times"
          className="p-button-outlined p-button-rounded p-button-danger ml-auto"
          onClick={options.onRemove}
        />
      </div>
    )
  }

  const messageContent = (
    <div className="flex align-items-center gap-2 w-full">
      <i className="p-message-icon pi pi-info-circle"></i>
      <div className="flex flex-grow-1 justify-content-between">
        <div>
          <strong>
            {t('suppliers.table.upload.dialog.uploadPanel.message.title')}
          </strong>
          <br />
          <span>
            {t('suppliers.table.upload.dialog.uploadPanel.message.description')}
          </span>
        </div>
        <div>
          <Button
            type="button"
            className="p-button-text p-button-rounded"
            icon={PrimeIcons.DOWNLOAD}
            onClick={() => downloadSample()}
          />
        </div>
      </div>
    </div>
  )

  const msgLimit = Constants.MaxSuppliers

  const messageContentLimit = (
    <div className="flex align-items-center gap-2 w-full">
      <i className="p-message-icon pi pi-exclamation-circle"></i>
      <div className="flex flex-grow-1 justify-content-between">
        <div>
          <strong>
            {t('suppliers.table.upload.dialog.uploadPanel.messageLimit.title')}
          </strong>
          <br />
          <span>
            {t(
              'suppliers.table.upload.dialog.uploadPanel.messageLimit.description',
              {
                msgLimit,
              },
            )}
          </span>
        </div>
      </div>
    </div>
  )

  // Fileupload options
  const uploadOptions = {
    className: 'hidden',
  }

  const cancelOptions = {
    className: 'hidden',
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className="flex flex-column gap-4">
        <Message
          className="w-full justify-content-start p-message my-0"
          severity="info"
          content={messageContent}
        />
        <Message
          className="w-full justify-content-start p-message my-0"
          severity="warn"
          content={messageContentLimit}
        />

        <div className="flex flex-column gap-2">
          <label
            htmlFor="csv"
            className={classNames('text-sm', {
              'p-error': !isFormFieldValid('csv'),
            })}
          >
            {t('suppliers.table.upload.dialog.uploadPanel.fields.csv')}
          </label>
          <FileUpload
            id="csv"
            uploadOptions={uploadOptions}
            cancelOptions={cancelOptions}
            multiple={false}
            itemTemplate={fileItemTemplate}
            accept=".csv"
            onRemove={() => formik.setFieldValue('csv', undefined)}
            onSelect={e => formik.setFieldValue('csv', e.files[0])}
            emptyTemplate={t(
              'suppliers.table.upload.dialog.uploadPanel.fileUpload.emptyTemplate',
            )}
          />
          {getFormErrorMessage('csv')}
        </div>
      </div>
    </form>
  )
}
